import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CaretDown, MagnifyingGlass, X } from '@phosphor-icons/react';

/**
 * Searchable single-select combobox — Material-styled, works with 300+ options.
 *
 * options: [{ value, label, description?, searchText? }]
 */
export default function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Type to search…',
  disabled = false,
  emptyMessage = 'No matches',
  variant = 'default',
  required = false,
  name,
  id,
  'aria-label': ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = (o.searchText || `${o.label} ${o.description || ''}`).toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setHighlight(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open, filtered]);

  const pick = useCallback((opt) => {
    if (opt?.disabled) return;
    onChange?.(opt?.value ?? '');
    setOpen(false);
    setQuery('');
  }, [onChange]);

  const onTriggerKey = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onSearchKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => {
        let next = Math.min(h + 1, Math.max(filtered.length - 1, 0));
        while (next < filtered.length - 1 && filtered[next]?.disabled) next += 1;
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => {
        let next = Math.max(h - 1, 0);
        while (next > 0 && filtered[next]?.disabled) next -= 1;
        return next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt && !opt.disabled) pick(opt);
    }
  };

  return (
    <div className={`tk-ss tk-ss-${variant}${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''}`} ref={rootRef}>
      {required ? (
        <input
          tabIndex={-1}
          aria-hidden="true"
          name={name}
          value={value || ''}
          required
          onChange={() => {}}
          style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none' }}
        />
      ) : null}
      <button
        type="button"
        id={id}
        className="tk-ss-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel || placeholder}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onTriggerKey}
      >
        <span className={`tk-ss-value${selected ? '' : ' is-placeholder'}`}>
          {selected ? (
            <>
              <span className="tk-ss-value-main">{selected.label}</span>
              {selected.description ? (
                <span className="tk-ss-value-sub">{selected.description}</span>
              ) : null}
            </>
          ) : (
            placeholder
          )}
        </span>
        <span className="tk-ss-actions">
          {selected && !disabled ? (
            <span
              role="button"
              tabIndex={-1}
              className="tk-ss-clear"
              aria-label="Clear"
              onClick={(e) => {
                e.stopPropagation();
                pick(null);
              }}
            >
              <X size={14} weight="bold" />
            </span>
          ) : null}
          <CaretDown size={14} weight="bold" className="tk-ss-caret" />
        </span>
      </button>

      {open && (
        <div className="tk-ss-panel" role="listbox">
          <div className="tk-ss-search">
            <MagnifyingGlass size={16} className="tk-ss-search-icon" />
            <input
              ref={inputRef}
              type="search"
              className="tk-ss-search-input"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
              onKeyDown={onSearchKey}
              placeholder={searchPlaceholder}
              autoComplete="off"
            />
            {query ? (
              <button type="button" className="tk-ss-clear" aria-label="Clear search" onClick={() => setQuery('')}>
                <X size={14} />
              </button>
            ) : null}
          </div>
          <div className="tk-ss-meta">
            {filtered.length} of {options.length}
          </div>
          <ul className="tk-ss-list" ref={listRef}>
            {filtered.length === 0 ? (
              <li className="tk-ss-empty">{emptyMessage}</li>
            ) : (
              filtered.map((opt, idx) => {
                const active = idx === highlight;
                const isSelected = String(opt.value) === String(value);
                const optDisabled = Boolean(opt.disabled);
                return (
                  <li key={opt.value === '' || opt.value == null ? `__opt-${idx}` : String(opt.value)}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={optDisabled}
                      disabled={optDisabled}
                      data-active={active ? 'true' : 'false'}
                      className={`tk-ss-option${active ? ' is-active' : ''}${isSelected ? ' is-selected' : ''}${optDisabled ? ' is-option-disabled' : ''}`}
                      onMouseEnter={() => setHighlight(idx)}
                      onClick={() => pick(opt)}
                    >
                      <span className="tk-ss-option-label">{opt.label}</span>
                      {opt.description ? (
                        <span className="tk-ss-option-desc">{opt.description}</span>
                      ) : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Build options from ticket objects for SearchableSelect. */
export function ticketsToOptions(tickets = [], formatExtra) {
  return (tickets || []).map((t) => {
    const extra = formatExtra ? formatExtra(t) : '';
    const subject = t.subject || '';
    const domain = t.domain || '';
    return {
      value: t.ticket_no,
      label: t.ticket_no,
      description: [domain, subject, extra].filter(Boolean).join(' · '),
      searchText: [t.ticket_no, subject, domain, t.status, t.category, t.service, ...(t.assignee_names || [])]
        .filter(Boolean)
        .join(' '),
    };
  });
}
